import React, { useState, useEffect, useCallback, useImperativeHandle } from 'react';
import { Table, Card, Tag, Space, Button, Tooltip, message } from 'antd';
import { EyeOutlined, DownloadOutlined, BankOutlined, EditOutlined } from '@ant-design/icons';
import { getAllStatements, exportStatementHistory } from '../api/accounting';
import { useNavigate } from 'react-router-dom';
import { useUserProfile } from "../hooks/useUserProfile";

const Statements = React.forwardRef(({ filters, selectedBrand = null, onEditStatement }, ref) => {
  const [loading, setLoading] = useState(false);
  const [statements, setStatements] = useState([]);
  const navigate = useNavigate();
  const { hasPermission } = useUserProfile();

  const fetchStatements = useCallback(async () => {
    setLoading(true);
    try {
      // Create API-compatible filter object
      const apiFilters = {};
      
      // Handle date range
      if (filters.dateRange && filters.dateRange.length === 2) {
        if (filters.dateRange[0]) {
          apiFilters.start_date = filters.dateRange[0].format('YYYY-MM-DD');
        }
        if (filters.dateRange[1]) {
          apiFilters.end_date = filters.dateRange[1].format('YYYY-MM-DD');
        }
      }
      
      // Handle bank filter if needed in the future
      if (filters.bank && filters.bank !== 'all') {
        apiFilters.bank = filters.bank;
      }
      
      // Handle brand filter
      if (selectedBrand) {
        apiFilters.brand = selectedBrand.id;
      }
      
      console.log('Fetching statements with filters:', apiFilters);
      const response = await getAllStatements(apiFilters);
      console.log('Statements response:', response);
      
      if (!response || !Array.isArray(response.results)) {
        console.error('Invalid response format:', response);
        message.error('Received invalid data format from server');
        setStatements([]);
        return;
      }
      
      // Transform the data to match our component's expected format
      const formattedStatements = response.results.map(statement => ({
        id: statement.id,
        bank: statement.bank_name || 'Unknown Bank',
        accountNumber: statement.account_number || '0000000000',
        statementDate: statement.statement_date,
        startDate: statement.start_date,
        endDate: statement.end_date,
        openingBalance: parseFloat(statement.opening_balance || 0),
        closingBalance: parseFloat(statement.closing_balance || 0),
        transactionCount: statement.transaction_count || 0,
        // Keep the original data for reference
        originalData: statement
      }));
      
      console.log('Formatted statements:', formattedStatements);
      setStatements(formattedStatements);
    } catch (error) {
      console.error('Error fetching statements:', error);
      message.error(`Failed to fetch statements: ${error.message || 'Unknown error'}`);
      setStatements([]);
    } finally {
      setLoading(false);
    }
  }, [filters, selectedBrand]);

  useEffect(() => {
    fetchStatements();
  }, [fetchStatements]);

  // Export functionality for Statement History
  const exportToExcel = useCallback(async (returnFile = false) => {
    try {
      // Prepare export filters according to API requirements
      const exportFilters = {
        brand: selectedBrand ? [selectedBrand.id] : [],
      };

      // Add date filter if available
      if (filters.dateRange && filters.dateRange.length === 2) {
        if (filters.dateRange[0]) {
          exportFilters.start_date = filters.dateRange[0].format('YYYY-MM-DD');
        }
        if (filters.dateRange[1]) {
          exportFilters.end_date = filters.dateRange[1].format('YYYY-MM-DD');
        }
      }

      console.log('Exporting with filters:', exportFilters);

      // Use the API function instead of direct fetch
      const response = await exportStatementHistory(exportFilters);
      
      if (returnFile) {
        // Return blob for email sharing
        const timestamp = new Date().toISOString().slice(0, 19).replace(/[:-]/g, '');
        const filename = `statement_history_export_${timestamp}.xlsx`;
        return new File([response.data], filename, {
          type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
        });
      } else {
        // Direct download
        const url = window.URL.createObjectURL(response.data);
        const link = document.createElement('a');
        link.href = url;
        
        const timestamp = new Date().toISOString().slice(0, 19).replace(/[:-]/g, '');
        link.download = `statement_history_export_${timestamp}.xlsx`;
        
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        window.URL.revokeObjectURL(url);
        
        message.success('Statement history exported successfully!');
      }
    } catch (error) {
      console.error('Export error:', error);
      message.error(`Export failed: ${error.message || 'Unknown error'}`);
      throw error;
    }
  }, [filters, selectedBrand]);

  // Expose export function via ref
  useImperativeHandle(ref, () => ({
    exportToExcel
  }));

  const getBalanceTag = (openingBalance, closingBalance) => {
    const difference = closingBalance - openingBalance;
    if (difference > 0) {
      return <Tag color="green">+${difference.toFixed(2)}</Tag>;
    } else if (difference < 0) {
      return <Tag color="red">${difference.toFixed(2)}</Tag>;
    } else {
      return <Tag color="default">$0.00</Tag>;
    }
  };

  const columns = [
    {
      title: 'Bank',
      dataIndex: 'bank',
      key: 'bank',
      sorter: (a, b) => a.bank.localeCompare(b.bank),
    },
    {
      title: 'Account Number',
      dataIndex: 'accountNumber',
      key: 'accountNumber',
      render: (accountNumber) => `...${accountNumber.slice(-4)}`,
    },
    {
      title: 'Statement Date',
      dataIndex: 'statementDate',
      key: 'statementDate',
      sorter: (a, b) => new Date(a.statementDate) - new Date(b.statementDate),
    },
    {
      title: 'Period',
      key: 'period',
      render: (_, record) => `${record.startDate} to ${record.endDate}`,
    },
    {
      title: 'Opening Balance',
      dataIndex: 'openingBalance',
      key: 'openingBalance',
      render: (balance) => `$${balance.toFixed(2)}`,
      sorter: (a, b) => a.openingBalance - b.openingBalance,
    },
    {
      title: 'Closing Balance',
      dataIndex: 'closingBalance',
      key: 'closingBalance',
      render: (balance) => `$${balance.toFixed(2)}`,
      sorter: (a, b) => a.closingBalance - b.closingBalance,
    },
    {
      title: 'Change',
      key: 'change',
      render: (_, record) => getBalanceTag(record.openingBalance, record.closingBalance),
    },
    {
      title: 'Transactions',
      dataIndex: 'transactionCount',
      key: 'transactionCount',
      sorter: (a, b) => a.transactionCount - b.transactionCount,
    },
    {
      title: 'Actions',
      key: 'actions',
      render: (_, record) => (
        <Space size="small">
          {/* <Tooltip title="View Transactions">
            <Button 
              type="text" 
              icon={<EyeOutlined />} 
              onClick={() => navigate(`/accounting/statements/view/${record.id}`)} 
            />
          </Tooltip> */}
          <Tooltip title="Edit Statement">
              <Button
                type="text"
                icon={<EditOutlined />}
                onClick={() => onEditStatement(record.id)}
              />
          </Tooltip>
          {/* <Tooltip title="View Bank Details">
            <Button 
              type="text" 
              icon={<BankOutlined />} 
              onClick={() => {
                if (record.originalData && record.originalData.bank) {
                  navigate(`/accounting/banks/view/${record.originalData.bank}`);
                } else {
                  message.warning('Bank details not available');
                }
              }} 
            />
          </Tooltip> */}
          <Tooltip title="Download Statement">
            <Button
              type="text"
              icon={<DownloadOutlined />}
              onClick={() => {
                message.info('Preparing statement download...');
                // In a real implementation, this would call an API endpoint to generate and download the statement
                // For now, we'll just show a message
                setTimeout(() => {
                  message.success('Statement downloaded successfully');
                }, 1500);
              }} 
            />
          </Tooltip>
        </Space>
      ),
    },
  ];

  return (
    <Card title="Bank Statements" bordered={false}>
      <Table
        columns={columns}
        dataSource={statements}
        rowKey="id"
        loading={loading}
        pagination={{ pageSize: 10 }}
      />
    </Card>
  );
});

// Add display name for the forwardRef component
Statements.displayName = 'Statements';

export default Statements;